import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CreditCard, Banknote, ArrowLeft, MapPin, Tag, X, Check, Gift, Store, Truck, Clock, Plus, ChevronRight, Trash2, Phone, CalendarDays } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { formatPrice, apiRequest, getSessionId } from '../lib/utils';
import { toast } from 'sonner';
import StampCard from '../components/StampCard';
import LoyaltyRewardModal from '../components/LoyaltyRewardModal';
import ContactInfoCard from '../components/checkout/ContactInfoCard';
import OrderTypeCard from '../components/checkout/OrderTypeCard';
import ScheduleCard from '../components/checkout/ScheduleCard';
import DeliveryTimeInfo from '../components/checkout/DeliveryTimeInfo';
import DeliveryAddressCard from '../components/checkout/DeliveryAddressCard';
import PickupNotesCard from '../components/checkout/PickupNotesCard';
import PaymentMethodCard from '../components/checkout/PaymentMethodCard';
import OrderSummarySection from '../components/checkout/OrderSummarySection';

const CheckoutPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { cart, cartTotal, clearCart, fetchCart, removeFromCart } = useCart();
  const { user, isAuthenticated } = useAuth();

  // Helper to get translated removed ingredients
  const getTranslatedRemoved = (item) => {
    if (!item.removed_ingredients?.length) return [];
    const lang = i18n.language;
    const engList = item.ingredients || [];
    const transMap = { it: item.ingredients_it, de: item.ingredients_de, ar: item.ingredients_ar };
    const translatedList = transMap[lang];
    if (!translatedList || translatedList.length !== engList.length) return item.removed_ingredients;
    return item.removed_ingredients.map(eng => {
      const idx = engList.indexOf(eng);
      return idx >= 0 ? translatedList[idx] : eng;
    });
  };
  
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [orderType, setOrderType] = useState('pickup');

  // Helper to get localized item name
  // Item names always shown in English (as created)
  const getLocalizedName = (item) => item.name;

  // Helper to get localized addon name
  const getLocalizedAddonName = (addon) => {
    const lang = i18n.language;
    if (lang === 'it' && addon.name_it) return addon.name_it;
    if (lang === 'de' && addon.name_de) return addon.name_de;
    if (lang === 'fr' && addon.name_fr) return addon.name_fr;
    if (lang === 'ar' && addon.name_ar) return addon.name_ar;
    return addon.name;
  };

  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [street, setStreet] = useState('');
  const [houseNumber, setHouseNumber] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [ringBellName, setRingBellName] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(null);

  // Loyalty state
  const [loyaltyStatus, setLoyaltyStatus] = useState(null);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [selectedReward, setSelectedReward] = useState(null);

  // Cart filler items
  const [fillerItems, setFillerItems] = useState([]);
  const fillerScrollRef = useRef(null);

  // Delivery fee state
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [checkingDelivery, setCheckingDelivery] = useState(false);

  // Scheduling state
  const [availableDates, setAvailableDates] = useState([]);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [timeSlots, setTimeSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const scrollFiller = (direction) => {
    if (fillerScrollRef.current) {
      fillerScrollRef.current.scrollBy({ left: direction * 220, behavior: 'smooth' });
    }
  };

  const sessionId = getSessionId();

  // Fetch filler items (drinks + appetizers)
  useEffect(() => {
    apiRequest('/menu')
      .then(items => {
        const fillers = items.filter(i =>
          i.category_id === 'cat-drinks' || i.category_id === '3c17ddf1-a840-4e62-8f75-2b7cc11270ef'
        ).filter(i => i.is_available !== false);
        setFillerItems(fillers);
      })
      .catch(() => {});
  }, []);

  const addFillerToCart = async (item) => {
    try {
      await apiRequest(`/cart/add?session_id=${sessionId}`, {
        method: 'POST',
        body: JSON.stringify({ menu_item_id: item.id, quantity: 1 })
      });
      await fetchCart();
      toast.success(`${item.name} ${t('checkout.addedToCart')}`);
    } catch {
      toast.error(t('checkout.addFailed'));
    }
  };
  
  // Fetch loyalty status on mount
  useEffect(() => {
    if (isAuthenticated) {
      apiRequest('/loyalty/status')
        .then(data => {
          setLoyaltyStatus(data);
          if (data.reward_available) {
            setShowRewardModal(true);
          }
        })
        .catch(() => {});
    }
  }, [isAuthenticated]);
  
  // Calculate discount
  const isDrinksOnly = cart.items.length > 0 && cart.items.every(item => item.category_id === 'cat-drinks');
  const discountAmount = isDrinksOnly ? 0 : (appliedPromo?.discount_amount || 0);
  const finalTotal = cartTotal - discountAmount;

  const applyPromoCode = async () => {
    if (!promoCode.trim()) {
      toast.error('Please enter a promo code');
      return;
    }
    
    setPromoLoading(true);
    try {
      const result = await apiRequest(`/promo-codes/validate?code=${promoCode}&order_total=${cartTotal}&session_id=${sessionId}`, { method: 'POST' });
      setAppliedPromo(result);
      toast.success(result.message);
    } catch (error) {
      toast.error(error.message || 'Invalid promo code');
      setAppliedPromo(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromoCode = () => {
    setAppliedPromo(null);
    setPromoCode('');
  };

  // Check delivery fee when address changes
  const checkDeliveryFee = async () => {
    if (orderType !== 'delivery' || !street.trim() || !city.trim()) {
      setDeliveryFee(0);
      setDeliveryInfo(null);
      return;
    }
    setCheckingDelivery(true);
    try {
      const address = `${street} ${houseNumber}, ${postalCode} ${city}, Switzerland`;
      const geoRes = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`);
      const geoData = await geoRes.json();
      if (geoData.length > 0) {
        const { lat, lon } = geoData[0];
        const result = await apiRequest(`/delivery/calculate?lat=${lat}&lng=${lon}&order_total=${cartTotal - discountAmount}`, { method: 'POST' });
        setDeliveryInfo(result);
        setDeliveryFee(result.fee || 0);
        if (result.pickup_only) {
          setOrderType('pickup');
          toast.info(t('delivery.outsideZone', 'Your address is outside our delivery area. Switched to pickup.'));
        }
      } else {
        setDeliveryInfo(null);
        setDeliveryFee(0);
      }
    } catch {
      setDeliveryInfo(null);
      setDeliveryFee(0);
    } finally {
      setCheckingDelivery(false);
    }
  };

  // Debounce delivery check when address fields change
  useEffect(() => {
    if (orderType !== 'delivery') {
      setDeliveryFee(0);
      setDeliveryInfo(null);
      return;
    }
    const timer = setTimeout(() => {
      if (street.trim() && city.trim()) {
        checkDeliveryFee();
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [street, houseNumber, postalCode, city, orderType, cartTotal, discountAmount]);

  // Fetch available dates on mount
  useEffect(() => {
    apiRequest('/scheduling/available-dates')
      .then(data => {
        const openDates = data.dates.filter(d => d.open);
        setAvailableDates(openDates);
        if (openDates.length > 0) {
          setScheduledDate(openDates[0].date);
        }
      })
      .catch(() => {});
  }, []);

  // Fetch time slots when date changes
  useEffect(() => {
    if (!scheduledDate) return;
    setLoadingSlots(true);
    setScheduledTime('');
    apiRequest(`/scheduling/slots?date=${scheduledDate}`)
      .then(data => {
        setTimeSlots(data.slots || []);
        if (data.slots?.length > 0) {
          setScheduledTime(data.slots[0]);
        }
      })
      .catch(() => setTimeSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [scheduledDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (cart.items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    // Validate phone: must be digits only, no empty
    const cleanPhone = phone.replace(/[^0-9+]/g, '');
    if (!cleanPhone || cleanPhone.replace(/\+/g, '').length < 5) {
      toast.error(t('checkout.invalidPhone') || 'Please enter a valid phone number (digits only)');
      return;
    }

    if (!scheduledDate || !scheduledTime) {
      toast.error(t('schedule.required', 'Please select a date and time for your order'));
      return;
    }

    setLoading(true);

    try {
      // Use XMLHttpRequest to avoid postMessage/DataCloneError from fetch interceptors
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('token');
      const orderData = {
        user_email: email,
        user_name: name,
        user_phone: phone,
        payment_method: paymentMethod,
        order_type: orderType,
        delivery_address: orderType === 'pickup' ? 'Pickup' : `${street} ${houseNumber}, ${postalCode} ${city}${ringBellName ? ` (${t('checkout.ringBellName')}: ${ringBellName})` : ''}`.trim(),
        notes: notes,
        promo_code: appliedPromo?.code || null,
        discount_amount: discountAmount,
        delivery_fee: orderType === 'delivery' ? deliveryFee : 0,
        redeem_loyalty: selectedReward || null,
        language: i18n.language || 'it',
        scheduled_date: scheduledDate || null,
        scheduled_time: scheduledTime || null
      };

      const response = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${baseUrl}/api/orders?session_id=${sessionId}`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.onload = () => {
          try {
            const data = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(data);
            } else {
              reject(new Error(data.detail || data.message || 'Failed to place order'));
            }
          } catch (e) {
            reject(new Error('Failed to place order'));
          }
        };
        xhr.onerror = () => reject(new Error('Network error. Please try again.'));
        xhr.send(JSON.stringify(orderData));
      });
      
      // Record promo code usage
      if (appliedPromo?.code) {
        try { await apiRequest(`/promo-codes/${appliedPromo.code}/use`, { method: 'POST' }); } catch(e) {}
      }

      if (paymentMethod === 'card' && response.redirect_url) {
        // Redirect to Stripe checkout
        window.location.href = response.redirect_url;
      } else {
        // COD - clear cart and redirect to success page
        await clearCart();
        toast.success('Order placed successfully!');
        navigate(`/order-success?order_id=${response.order_id}`);
      }
    } catch (error) {
      const msg = error?.message || 'Failed to place order';
      if (msg.includes('postMessage') || msg.includes('DataCloneError') || msg.includes('could not be cloned')) {
        toast.error('Failed to place order. Please try again.');
      } else {
        toast.error(msg);
      }
      setLoading(false);
    }
  };

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen py-16">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h1 className="text-3xl font-bold mb-4">{t('cart.empty')}</h1>
          <p className="text-zinc-400 mb-8">{t('cart.emptyDesc')}</p>
          <Link to="/menu">
            <Button className="bg-[#FFC107] text-black hover:bg-[#F59E0B]">
              {t('hero.viewMenu')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const sectionProps = {
    addFillerToCart,
    appliedPromo,
    applyPromoCode,
    availableDates,
    cart,
    cartTotal,
    checkingDelivery,
    city,
    deliveryFee,
    deliveryInfo,
    discountAmount,
    email,
    fillerItems,
    fillerScrollRef,
    finalTotal,
    getLocalizedAddonName,
    getLocalizedName,
    getTranslatedRemoved,
    houseNumber,
    i18n,
    isAuthenticated,
    isDrinksOnly,
    loading,
    loadingSlots,
    loyaltyStatus,
    name,
    notes,
    orderType,
    paymentMethod,
    phone,
    postalCode,
    promoCode,
    promoLoading,
    removeFromCart,
    removePromoCode,
    ringBellName,
    scheduledDate,
    scheduledTime,
    scrollFiller,
    selectedReward,
    setCity,
    setEmail,
    setHouseNumber,
    setName,
    setNotes,
    setOrderType,
    setPaymentMethod,
    setPhone,
    setPostalCode,
    setPromoCode,
    setRingBellName,
    setScheduledDate,
    setScheduledTime,
    setStreet,
    street,
    t,
    timeSlots,
    user,
  };

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/menu" className="inline-flex items-center text-zinc-400 hover:text-white mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('checkout.backToMenu')}
          </Link>
          <h1 className="text-4xl font-bold uppercase tracking-tight text-white font-['Barlow_Condensed']">
            {t('checkout.title')}
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Order Form */}
          <div className="order-2 lg:order-1">
            <form onSubmit={handleSubmit} className="space-y-6">
              <ContactInfoCard {...sectionProps} />
              <OrderTypeCard {...sectionProps} />
              <ScheduleCard {...sectionProps} />
              <DeliveryTimeInfo {...sectionProps} />
              <DeliveryAddressCard {...sectionProps} />
              <PickupNotesCard {...sectionProps} />
              <PaymentMethodCard {...sectionProps} />

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 bg-[#FFC107] text-black hover:bg-[#F59E0B] font-bold uppercase tracking-wide text-lg btn-glow"
                data-testid="place-order-button"
              >
                {loading ? t('checkout.processing') : paymentMethod === 'card' ? t('checkout.payNow') : t('checkout.placeOrder')}
              </Button>
            </form>
          </div>

          {/* Order Summary */}
          <OrderSummarySection {...sectionProps} />
        </div>
      </div>
      {/* Loyalty Reward Selection Modal */}
      <LoyaltyRewardModal
        isOpen={showRewardModal}
        onClose={() => setShowRewardModal(false)}
        onSelect={(rewardId) => setSelectedReward(rewardId)}
      />
    </div>
  );
};

export default CheckoutPage;
